import { useAuth0 } from '@auth0/auth0-react';
import { useAvailableAccounts, useOrganisation, usePermissions } from '@my/directory-api/hooks';
import { useAccount } from '@my/platform/account';
import { clearSupportSession, useSupportSession } from '@my/platform/support';
import { AccountSwitcher, Sidebar, SupportAccountLabel, type SidebarItem } from '@my/ui';
import { BarChart3, Box, Building2, CalendarDays, MapPin, Receipt, Settings, Users } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { useUiStore } from '@/stores/ui';

function getDisplayName(user: ReturnType<typeof useAuth0>['user']): string {
  if (!user) return 'Demo User';
  const given = user.given_name ?? '';
  const family = user.family_name ?? '';
  const full = `${given} ${family}`.trim();
  return full || user.nickname || user.email || 'Demo User';
}

/**
 * Tms-flavoured `<Sidebar>` from `@my/ui`. Holds the Tms menu config and
 * bridges Auth0 → Sidebar (user, sign-out). Depot/Expenses will have their
 * own equivalents under `apps/<app>/src/layout/AppSidebar.tsx`.
 *
 * Account switching: composes `useAvailableAccounts` (directory-api),
 * `useAccount` (platform), and `<AccountSwitcher>` (ui) into the Sidebar's
 * `headerSlot`. Switching an account updates the platform store and refreshes
 * the Auth0 access token bound to the new `account_id`.
 */
export function AppSidebar() {
  const { isSupportSession, session: supportSession } = useSupportSession();
  const { user, logout, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { currentAccountId, setCurrentAccountId } = useAccount();
  const isOpen = useUiStore((s) => s.isSidebarOpen);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const { t } = useTranslation('navigation');

  const accountsQuery = useAvailableAccounts({
    // Not during a support session. The switcher below is REPLACED by a
    // read-only label then, so the list is never rendered — and the request
    // 404s anyway, twice, because `retry: 1` retries a deliberate answer.
    // The label was the fix for the "Loading…" that this used to cause; this
    // is the other half of it, the request that did not need making.
    enabled: isAuthenticated && !isSupportSession,
  });
  const accounts = useMemo(
    () => (accountsQuery.data ?? []).map((a) => ({ id: a.id, name: a.name })),
    [accountsQuery.data],
  );

  const handleSwitch = async (accountId: string) => {
    setCurrentAccountId(accountId);
    // Re-mint the access token bound to the newly selected account.
    // `cacheMode: 'off'` forces Auth0 to round-trip rather than returning the
    // previous token from cache. Match the original Tms scope so downstream
    // calls keep working.
    await getAccessTokenSilently({
      authorizationParams: {
        account_id: accountId,
        scope: 'openid profile email offline_access',
        subscription: 'tms',
      },
      cacheMode: 'off',
    });
  };

  // Every item asks the same question: does this user have the capability
  // behind it? The list comes from the server (role's ceiling, narrowed by the
  // user's own functional roles), so the menu cannot disagree with what the API
  // will allow — and adding "a Manager sees two Settings pages" is a change to
  // a preset, not a hunt for role literals through the components.
  //
  // `can` is false while the profile loads, so nothing flashes into view and
  // straight back out.
  const { can, canAny, role } = usePermissions();

  // Products is an organisation choice (the switch on Settings → Cargo
  // Definitions), not a capability: the page appears for whoever may reach
  // Central Configuration, and only once the organisation has switched it on.
  const organisationId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const organisationQuery = useOrganisation(organisationId);
  const productsOn = organisationQuery.data?.useProducts ?? false;

  const items: SidebarItem[] = [
    {
      id: 'dashboard',
      label: t('dashboard'),
      icon: BarChart3,
      href: '/dashboard',
      matchRoot: true,
      hidden: !can('dashboard'),
    },
    { id: 'orders', label: t('orders'), icon: Box, href: '/orders', hidden: !can('orders') },
    {
      id: 'analytics',
      label: t('analytics'),
      icon: BarChart3,
      href: '/analytics',
      hidden: !can('analytics'),
    },
    {
      id: 'roster',
      label: t('roster'),
      icon: CalendarDays,
      href: '/roster',
      hidden: !can('roster'),
    },
    {
      id: 'locations',
      label: t('locations'),
      icon: MapPin,
      href: '/locations',
      hidden: !can('locations'),
    },
    {
      id: 'contacts',
      label: t('contacts'),
      icon: Users,
      // A group is a heading, not a destination — it appears when it has
      // something to show rather than needing a capability of its own.
      hidden: !can('customers') && !can('suppliers'),
      children: [
        {
          id: 'customers',
          label: t('customers'),
          href: '/customers',
          hidden: !can('customers'),
        },
        {
          id: 'suppliers',
          label: t('suppliers'),
          href: '/suppliers',
          hidden: !can('suppliers'),
        },
      ],
    },
    {
      id: 'finance',
      label: t('finance'),
      icon: Receipt,
      hidden: !can('invoicing'),
      children: [
        {
          id: 'invoicing',
          label: t('invoicing'),
          href: '/finance/invoicing',
          hidden: !can('invoicing'),
        },
      ],
    },
    {
      id: 'settings',
      label: t('settings'),
      icon: Settings,
      // Settings capabilities come from the role alone and are not per-user
      // toggles: it is where roles are granted, so making it individually
      // revocable would let somebody lock everyone out of the screen that
      // fixes it. A Manager therefore sees the group with two pages in it.
      hidden: !canAny('settings.'),
      children: [
        {
          id: 'organisation',
          label: t('organisation'),
          href: '/settings/organisation',
          hidden: !can('settings.organisation'),
        },
        {
          id: 'configuration',
          label: t('centralConfiguration'),
          href: '/settings/configuration',
          hidden: !can('settings.centralConfiguration'),
        },
        {
          id: 'cargoDefinitions',
          label: t('cargoDefinitions'),
          href: '/settings/cargo-definitions',
          hidden: !can('settings.centralConfiguration'),
        },
        {
          id: 'products',
          label: t('products'),
          href: '/settings/products',
          hidden: !can('settings.centralConfiguration') || !productsOn,
        },
        {
          id: 'usersAndAccess',
          label: t('usersAndAccess'),
          href: '/settings/users-and-access',
          hidden: !can('settings.usersAndAccess'),
        },
        {
          id: 'resources',
          label: t('resources'),
          href: '/settings/resources',
          hidden: !can('settings.resources'),
        },
        {
          id: 'documents',
          label: t('documents'),
          href: '/settings/documents',
          hidden: !can('settings.documents'),
        },
        {
          id: 'workflows',
          label: t('workflows'),
          href: '/settings/workflows',
          hidden: !can('settings.workflows'),
        },
        {
          id: 'tripCheckpoints',
          label: t('tripCheckpoints'),
          href: '/settings/trip-checkpoints',
          // TMS Mobile module on, and Owner/Admin — see AppCapabilities.
          hidden: !can('settings.tripCheckpoints'),
        },
        {
          id: 'auditLog',
          label: t('auditLog'),
          href: '/settings/audit-log',
          hidden: !can('settings.auditLog'),
        },
        {
          id: 'import',
          label: t('import'),
          href: '/settings/import',
          hidden: !can('settings.import'),
        },
      ],
    },
  ];

  return (
    <Sidebar
      isOpen={isOpen}
      onToggle={toggle}
      brand={{ title: 'My TMS', icon: Building2 }}
      headerSlot={
        // The switcher is replaced, not hidden, during a Stratum support
        // session. It moves between the USER'S OWN accounts and reads
        // `/accounts/available`, which 404s for a support identity that is a
        // member of none — so it hung on "Loading…" forever. The read-only
        // label keeps the brand block aligned and still answers "whose data is
        // this"; changing customer stays in the support bar, because two
        // controls offering the same switch is how they end up disagreeing.
        isSupportSession ? (
          <SupportAccountLabel accountName={supportSession?.accountName} />
        ) : (
          <AccountSwitcher
            accounts={accounts}
            currentAccountId={currentAccountId}
            onSwitch={handleSwitch}
            isLoading={accountsQuery.isPending}
          />
        )
      }
      items={items}
      // Lower-cased to keep the footer reading as it did when the role came
      // from the token claim; blank until the profile lands.
      user={{ displayName: getDisplayName(user), role: role?.toLowerCase() ?? '' }}
      onSignOut={() => {
        // Clear FIRST. A support session lives in sessionStorage, which survives
        // a sign-out on the same tab — leaving the next person to sign in here
        // carrying somebody else's customer, and their screens pointed at that
        // customer's organisation.
        clearSupportSession({ silent: true });
        logout({ logoutParams: { returnTo: window.location.origin } });
      }}
    />
  );
}
